/**
 * UltraAdminStandRequestsPage
 * ───────────────────────────
 * Müşterilerin Customer Dashboard'ta gönderdiği "Stand Alanı Talepleri"ni
 * (modüler / özel tasarım) tek merkezi sayfada listeler.
 *
 * Data kaynağı: GET /api/ultra-admin/tenant-stand-summary
 *   - Yalnızca `requested_area` veya `requested_design_type` dolu olan tenant'lar
 *     göstergeye girer ("henüz talep göndermeyenler" filtrelenir).
 *
 * Özellikler:
 *   • Durum filtresi: Tümü / Bekliyor (stand_selected ≠ true) / Atandı
 *   • Tasarım tipi filtresi: Modüler / Özel
 *   • Arama (şirket adı / email / ülke)
 *   • Kart: tasarım tipi rozeti · m² · layout · tarih · Custom PDF varsa "View PDF"
 *   • "Stand Ata" butonu → /ultra-admin/blue-zone (atama gridine yönlendirir)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/apiClient';
import {
  Package, Search, FileText, CheckCircle2, Clock, Globe, Mail, ArrowRight, Filter, ExternalLink, MessageSquare, X, MapPin,
} from 'lucide-react';
import AdminSidebar from '../components/AdminSidebar';
import UltraAdminTopbar from '../components/UltraAdminTopbar';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

export default function UltraAdminStandRequestsPage() {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const navigate = useNavigate();

  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  // Phase 86Q — Default to 'pending' so ASSIGNED rows do NOT clutter the
  // active work-queue. Admin can still flip to 'assigned' or 'all' via chips.
  const [filterStatus, setFilterStatus] = useState('pending');  // pending | assigned | all
  const [filterDesign, setFilterDesign] = useState('all');  // all | modular | custom
  const [filterHall, setFilterHall]     = useState('all');  // all | <hall_name>
  const [filterZone, setFilterZone]     = useState('all');  // all | blue | green
  const [noteModal, setNoteModal]       = useState(null);   // null | { tenantName, country, text }

  // Auth guard
  useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token') || localStorage.getItem('cop31_token');
    if (!token) navigate('/giris?tab=ultra');
  }, [navigate]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/ultra-admin/tenant-stand-summary');
      const data = Array.isArray(r.data) ? r.data : [];
      // Only rows that have actually submitted via "Submit Area Request" button.
      // `stand_request_submitted_at` is set only by PUT /customer/stand-request,
      // never by onboarding wizard — so it cleanly distinguishes a real request
      // from data that was just collected during onboarding.
      const withRequest = data.filter(t => !!t.stand_request_submitted_at);
      setItems(withRequest);
    } catch (e) {
      toast.error(tr ? 'Talepler yüklenemedi.' : 'Failed to load requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    const list = items.filter(t => {
      if (filterStatus === 'pending'  && t.status === 'stand_selected')  return false;
      if (filterStatus === 'assigned' && (t.status !== 'stand_selected' || t.customer_approval_status === 'approved')) return false;
      if (filterStatus === 'approved' && t.customer_approval_status !== 'approved') return false;
      if (filterDesign === 'modular' && t.requested_design_type !== 'modular') return false;
      if (filterDesign === 'custom'  && t.requested_design_type !== 'custom')  return false;
      if (filterHall !== 'all' && (t.assigned_hall_name || '__none__') !== filterHall) return false;
      if (filterZone !== 'all') {
        // Match either canonical "blue_zone"/"green_zone" or shortened forms.
        const z = (t.assigned_zone || '').toLowerCase();
        if (filterZone === 'blue'  && !z.includes('blue'))  return false;
        if (filterZone === 'green' && !z.includes('green')) return false;
      }
      if (q) {
        const hay = [t.company_name, t.email, t.country, t.slug].filter(Boolean).join(' ').toLocaleLowerCase('tr-TR');
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // Phase 86R — Sort: assigned rows first by assigned_at DESC (most recent
    // allocation first), pending rows after by submitted_at DESC. This puts
    // the freshest activity at the top so admins can monitor latest changes.
    return list.sort((a, b) => {
      const aAssigned = a.status === 'stand_selected';
      const bAssigned = b.status === 'stand_selected';
      if (aAssigned !== bAssigned) return aAssigned ? -1 : 1;
      const aDate = aAssigned ? (a.assigned_at || a.stand_request_submitted_at || '') : (a.stand_request_submitted_at || '');
      const bDate = bAssigned ? (b.assigned_at || b.stand_request_submitted_at || '') : (b.stand_request_submitted_at || '');
      return (bDate || '').localeCompare(aDate || '');
    });
  }, [items, search, filterStatus, filterDesign, filterHall, filterZone]);

  // Unique halls (assigned only) for the filter dropdown.
  const halls = useMemo(() => {
    const set = new Set();
    items.forEach(t => { if (t.assigned_hall_name) set.add(t.assigned_hall_name); });
    return Array.from(set).sort();
  }, [items]);

  // Available zones across the assigned cards — drives the Zone dropdown.
  const zones = useMemo(() => {
    const set = new Set();
    items.forEach(t => {
      const z = (t.assigned_zone || '').toLowerCase();
      if (z.includes('blue'))  set.add('blue');
      if (z.includes('green')) set.add('green');
    });
    return Array.from(set);
  }, [items]);

  const counts = useMemo(() => ({
    total:    items.length,
    pending:  items.filter(t => t.status !== 'stand_selected').length,
    assigned: items.filter(t => t.status === 'stand_selected' && t.customer_approval_status !== 'approved').length,
    approved: items.filter(t => t.customer_approval_status === 'approved').length,
  }), [items]);

  const handleViewPdf = async (tenantId) => {
    try {
      const r = await apiClient.get(`/ultra-admin/custom-design-pdf/${tenantId}`);
      if (r.data?.url) window.open(r.data.url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'PDF açılamadı.' : 'Failed to open PDF.'));
    }
  };

  const handleMarkReviewing = async (tenantId) => {
    try {
      await apiClient.put(`/ultra-admin/stand-requests/${tenantId}/review`, {});
      toast.success(tr ? 'Talep incelemeye alındı.' : 'Request marked as under review.');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'İşlem başarısız.' : 'Action failed.'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <div className="flex-1 flex flex-col">
        <UltraAdminTopbar />

        <div className="px-6 pt-5">
          <PageIntro {...getPageIntro('stand-requests', language)} />
        </div>

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 bg-white">
          {/* Phase 86R++ — Title row above, stat-pill filter row below.
              Earlier the pills were on the right side and got cut off /
              wrapped on smaller widths. Now they live on their own line so
              they're always visible right under the page title. */}
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Package className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {tr ? 'Stand Alanı Talepleri' : 'Stand Area Requests'}
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {tr
                  ? 'Müşterilerin gönderdiği modüler ve özel tasarım alan talepleri — yer ataması yapılana kadar buradan takip edin.'
                  : 'Area requests submitted by customers (modular + custom design) — track and allocate from here.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="stat-pill-row">
            <Stat label={tr ? 'Toplam' : 'Total'}     value={counts.total}    color="gray"    testid="stat-total"
                  active={filterStatus === 'all'    && filterDesign === 'all'} onClick={() => { setFilterStatus('all'); setFilterDesign('all'); }} />
            <Stat label={tr ? 'Bekliyor' : 'Pending'} value={counts.pending}  color="amber"   testid="stat-pending"
                  active={filterStatus === 'pending'}  onClick={() => { setFilterStatus('pending');  setFilterDesign('all'); }} />
            <Stat label={tr ? 'Atandı' : 'Assigned'}  value={counts.assigned} color="emerald" testid="stat-assigned"
                  active={filterStatus === 'assigned'} onClick={() => { setFilterStatus('assigned'); setFilterDesign('all'); }} />
            <Stat label={tr ? 'Onaylandı' : 'Approved'} value={counts.approved} color="green" testid="stat-approved"
                  active={filterStatus === 'approved'} onClick={() => { setFilterStatus('approved'); setFilterDesign('all'); }} />
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-100 bg-white">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={tr ? 'Şirket / email / ülke ara...' : 'Search company / email / country...'}
                className="w-full pl-10 pr-3 py-2 text-sm rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                data-testid="stand-requests-search-input"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="text-sm rounded-xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-indigo-300"
              data-testid="stand-requests-status-filter"
            >
              <option value="all">{tr ? 'Tüm Durumlar' : 'All Statuses'}</option>
              <option value="pending">{tr ? 'Bekliyor' : 'Pending'}</option>
              <option value="assigned">{tr ? 'Atandı' : 'Assigned'}</option>
              <option value="approved">{tr ? 'Onaylandı' : 'Approved'}</option>
            </select>
            <select
              value={filterDesign}
              onChange={(e) => setFilterDesign(e.target.value)}
              className="text-sm rounded-xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-indigo-300"
              data-testid="stand-requests-design-filter"
            >
              <option value="all">{tr ? 'Tüm Tasarımlar' : 'All Designs'}</option>
              <option value="modular">{tr ? 'Modüler' : 'Modular'}</option>
              <option value="custom">{tr ? 'Özel Tasarım' : 'Custom Design'}</option>
            </select>
            {halls.length > 0 && (
              <select
                value={filterHall}
                onChange={(e) => setFilterHall(e.target.value)}
                className="text-sm rounded-xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-indigo-300"
                data-testid="stand-requests-hall-filter"
              >
                <option value="all">{tr ? 'Tüm Salonlar' : 'All Halls'}</option>
                {halls.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            )}
            {zones.length > 0 && (
              <select
                value={filterZone}
                onChange={(e) => setFilterZone(e.target.value)}
                className="text-sm rounded-xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-indigo-300"
                data-testid="stand-requests-zone-filter"
              >
                <option value="all">{tr ? 'Tüm Bölgeler' : 'All Zones'}</option>
                {zones.includes('blue')  && <option value="blue">{tr ? 'Mavi Bölge'  : 'Blue Zone'}</option>}
                {zones.includes('green') && <option value="green">{tr ? 'Yeşil Bölge' : 'Green Zone'}</option>}
              </select>
            )}
            <button
              onClick={fetchItems}
              className="text-sm rounded-xl border border-gray-200 px-3 py-2 hover:bg-gray-50 inline-flex items-center gap-1.5"
              data-testid="stand-requests-refresh"
            >
              <Filter className="h-4 w-4 text-gray-500" />
              {tr ? 'Yenile' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="text-center py-20 text-gray-400 text-sm">{tr ? 'Yükleniyor...' : 'Loading...'}</div>
          ) : filtered.length === 0 ? (
            <EmptyState tr={tr} hasAny={items.length > 0} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="stand-requests-grid">
              {filtered.map(t => (
                <RequestCard
                  key={t.id}
                  t={t}
                  tr={tr}
                  onViewPdf={() => handleViewPdf(t.id)}
                  onAllocate={() => {
                    // Deep-link: if already allocated, jump straight to that hall
                    // editor with the stand pre-selected & zoomed.
                    if (t.assigned_hall_id && t.assigned_hall_stand_id) {
                      navigate(`/ultra-admin/halls/${t.assigned_hall_id}?focus_stand=${t.assigned_hall_stand_id}`);
                    } else {
                      navigate('/ultra-admin/halls');
                    }
                  }}
                  onMarkReviewing={() => handleMarkReviewing(t.id)}
                  onOpenNote={async () => {
                    setNoteModal({
                      tenantName: t.company_name,
                      country: t.country,
                      text: t.stand_request_notes,
                    });
                    // Clear the per-card 'N new' badge — and the bell counter — for this tenant
                    if (t.unread_note_count > 0) {
                      try {
                        await apiClient.put(`/ultra-admin/stand-requests/${t.id}/mark-seen`, {});
                        fetchItems();
                      } catch { /* silent */ }
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Note modal — read-only for admin (full text) */}
        {noteModal && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setNoteModal(null)}
            data-testid="admin-note-modal"
          >
            <div
              className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-amber-600" />
                  <div>
                    <h3 className="font-bold text-gray-900 text-base">{tr ? 'Müşteri Notu' : 'Customer Note'}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {noteModal.tenantName}{noteModal.country ? ` · ${noteModal.country}` : ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setNoteModal(null)}
                  className="text-gray-400 hover:text-gray-700"
                  data-testid="admin-note-modal-close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-5 bg-amber-50/30">
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {noteModal.text || (tr ? '(Boş not)' : '(Empty note)')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color, testid, active = false, onClick }) {
  // Phase 86R+ — Stat pills are now click-to-filter buttons. When active,
  // the pill becomes the strong-saturation variant (filled bg, white text)
  // so the operator sees which filter is applied at a glance.
  const inactive = {
    gray:    'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200',
    amber:   'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
    green:   'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
    indigo:  'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
    fuchsia: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-100',
  }[color] || '';
  const activeCls = {
    gray:    'bg-gray-700 text-white border-gray-700 ring-2 ring-gray-300',
    amber:   'bg-amber-500 text-white border-amber-500 ring-2 ring-amber-200',
    emerald: 'bg-emerald-600 text-white border-emerald-600 ring-2 ring-emerald-200',
    green:   'bg-green-600 text-white border-green-600 ring-2 ring-green-200',
    indigo:  'bg-indigo-600 text-white border-indigo-600 ring-2 ring-indigo-200',
    fuchsia: 'bg-fuchsia-600 text-white border-fuchsia-600 ring-2 ring-fuchsia-200',
  }[color] || '';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active ? 'true' : 'false'}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer ${active ? activeCls : inactive}`}
    >
      <span className="opacity-80 font-semibold">{label}</span>
      <span>{value}</span>
    </button>
  );
}

function RequestCard({ t, tr, onViewPdf, onAllocate, onMarkReviewing, onOpenNote }) {
  const isAssigned   = t.status === 'stand_selected';
  const isCustom     = t.requested_design_type === 'custom';
  const reqStatus    = t.stand_request_status || 'pending';      // pending | under_review | allocated
  const statusLabel  = isAssigned ? (tr ? 'Atandı' : 'Allocated')
                     : reqStatus === 'under_review' ? (tr ? 'İnceleniyor' : 'Under Review')
                     : (tr ? 'Atama Bekliyor' : 'Awaiting Allocation');
  const statusIcon   = isAssigned ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                     : reqStatus === 'under_review' ? <Clock className="h-3.5 w-3.5 text-blue-600" />
                     : <Clock className="h-3.5 w-3.5 text-amber-600" />;
  const stripCls     = isAssigned ? 'bg-emerald-50' : reqStatus === 'under_review' ? 'bg-blue-50' : 'bg-amber-50';
  const stripText    = isAssigned ? 'text-emerald-700' : reqStatus === 'under_review' ? 'text-blue-700' : 'text-amber-700';
  const borderCls    = isAssigned ? 'border-emerald-200' : reqStatus === 'under_review' ? 'border-blue-200' : 'border-amber-200';
  const submittedAt  = t.stand_request_submitted_at
    ? new Date(t.stand_request_submitted_at).toLocaleDateString(tr ? 'tr-TR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;
  const assignedAt   = t.assigned_at
    ? new Date(t.assigned_at).toLocaleDateString(tr ? 'tr-TR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;
  const reviewedAt   = t.stand_request_reviewed_at
    ? new Date(t.stand_request_reviewed_at).toLocaleDateString(tr ? 'tr-TR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  return (
    <div className={`bg-white rounded-2xl border ${borderCls} overflow-hidden hover:shadow-md transition-shadow`} data-testid={`stand-request-card-${t.id}`}>
      {/* Top strip */}
      <div className={`px-4 py-2 flex items-center justify-between ${stripCls}`}>
        <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${stripText}`}>
          {statusIcon}
          <span>{statusLabel}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {t.unread_note_count > 0 && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500 text-white inline-flex items-center gap-1 animate-pulse"
              title={tr ? 'Bu talep için okunmamış müşteri bildirimi' : 'Unread customer updates on this request'}
              data-testid={`unread-badge-${t.id}`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-white" />
              {t.unread_note_count} {tr ? 'yeni bildirim' : 'new update' + (t.unread_note_count > 1 ? 's' : '')}
            </span>
          )}
          {t.stand_paid && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white inline-flex items-center gap-1"
              title={tr ? 'Stand siparişi ödendi' : 'Stand order paid'}
              data-testid={`paid-badge-${t.id}`}
            >
              <CheckCircle2 className="h-3 w-3" />
              {tr ? 'Ödendi' : 'Paid'}
            </span>
          )}
          {/* Phase 86W — Customer-side approval/change-request status pill.
              Shown only on assigned cards: tells the admin whether the
              customer has accepted the allocation, is still pending, or
              has filed an open change request. */}
          {isAssigned && t.pending_change_count > 0 && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white inline-flex items-center gap-1 animate-pulse"
              title={tr ? 'Müşteri stand değişikliği talep etti' : 'Customer requested a stand change'}
              data-testid={`change-req-badge-${t.id}`}
            >
              <ArrowRight className="h-3 w-3" />
              {t.pending_change_count > 1
                ? `${t.pending_change_count} ${tr ? 'değişiklik' : 'changes'}`
                : (tr ? 'Değişiklik İstendi' : 'Change Requested')}
            </span>
          )}
          {isAssigned && t.pending_change_count === 0 && t.customer_approval_status === 'approved' && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-600 text-white inline-flex items-center gap-1"
              title={tr ? 'Müşteri stand alanını onayladı' : 'Customer approved the stand area'}
              data-testid={`customer-approved-badge-${t.id}`}
            >
              <CheckCircle2 className="h-3 w-3" />
              {tr ? 'Müşteri Onayladı' : 'Customer Approved'}
            </span>
          )}
          {isAssigned && t.pending_change_count === 0 && t.customer_approval_status !== 'approved' && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-300 inline-flex items-center gap-1"
              title={tr ? 'Müşteri stand alanını henüz onaylamadı' : 'Customer has not approved the stand area yet'}
              data-testid={`customer-pending-badge-${t.id}`}
            >
              <Clock className="h-3 w-3" />
              {tr ? 'Onay Bekliyor' : 'Awaiting Approval'}
            </span>
          )}
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isCustom ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-indigo-100 text-indigo-700'}`}>
            {isCustom ? (tr ? 'Özel Tasarım' : 'Custom Design') : (tr ? 'Modüler' : 'Modular')}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        {/* Tenant identity */}
        <h3 className="font-bold text-gray-900 text-sm leading-tight mb-1 flex items-center gap-1.5">
          {t.company_name || '—'}
          {t.country && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
              <Globe className="h-2.5 w-2.5" />{t.country}
            </span>
          )}
        </h3>
        {isAssigned && (t.assigned_stand_no || t.assigned_hall_name) ? (
          <p className="text-xs text-emerald-700 font-bold flex items-center gap-1 mb-3 truncate" data-testid={`assigned-loc-${t.id}`}>
            <MapPin className="h-3 w-3" />
            {t.assigned_hall_name ? `${t.assigned_hall_name}` : (tr ? 'Salon —' : 'Hall —')}
            {t.assigned_stand_no ? <span className="text-emerald-900"> · Stand {t.assigned_stand_no}</span> : null}
          </p>
        ) : (
          t.email && (
            <p className="text-xs text-gray-500 flex items-center gap-1 mb-3 truncate">
              <Mail className="h-3 w-3" />{t.email}
            </p>
          )
        )}

        {/* Request details */}
        <div className="grid grid-cols-3 gap-2 text-center mb-3">
          <Detail label={tr ? 'Alan' : 'Area'}     value={t.requested_area ? `${t.requested_area} m²` : '—'} />
          <Detail label={tr ? 'Layout' : 'Layout'} value={t.requested_layout_type || '—'} capitalize />
          <Detail
            label={isAssigned ? (tr ? 'Atandı' : 'Assigned') : (tr ? 'Talep' : 'Submitted')}
            value={isAssigned
              ? (assignedAt || submittedAt || '—')
              : (submittedAt || '—')}
          />
        </div>
        {reviewedAt && (
          <p className="text-[10px] text-blue-600 font-semibold mb-3 -mt-1">
            {tr ? `İncelemeye alındı: ${reviewedAt}` : `Reviewed: ${reviewedAt}`}
          </p>
        )}

        {/* Customer note — click to expand into modal */}
        {t.stand_request_notes && (
          <button
            type="button"
            onClick={onOpenNote}
            className="w-full bg-amber-50 hover:bg-amber-100 border-l-4 border-amber-400 rounded-r-lg px-3 py-2 mb-3 text-left transition-colors group"
            data-testid={`note-${t.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-bold text-amber-700 uppercase tracking-wider mb-0.5">
                  {tr ? 'Müşteri Notu' : 'Customer Note'}
                </p>
                <p className="text-xs text-amber-900 leading-relaxed line-clamp-2">{t.stand_request_notes}</p>
              </div>
              <span className="text-[10px] font-bold text-amber-700 group-hover:text-amber-900 flex-shrink-0 mt-0.5">
                {tr ? 'Tamamı →' : 'Read all →'}
              </span>
            </div>
          </button>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {isCustom && t.has_custom_design_pdf && (
            <button
              onClick={onViewPdf}
              data-testid={`view-pdf-${t.id}`}
              className="inline-flex items-center justify-center gap-1 bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-700 text-xs font-bold px-3 py-2 rounded-xl border border-fuchsia-200 transition-colors"
              title={t.custom_design_pdf_name || ''}
            >
              <FileText className="h-3.5 w-3.5" />
              {tr ? 'PDF\'yi Gör' : 'View PDF'}
            </button>
          )}
          {!isAssigned && reqStatus === 'pending' && (
            <button
              onClick={onMarkReviewing}
              data-testid={`mark-reviewing-${t.id}`}
              className="inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold px-3 py-2 rounded-xl border border-blue-200 transition-colors"
            >
              <Clock className="h-3.5 w-3.5" />
              {tr ? 'İncelemeye Al' : 'Start Review'}
            </button>
          )}
          {!isAssigned && (
            <button
              onClick={onAllocate}
              data-testid={`allocate-${t.id}`}
              className="ml-auto inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3 py-2 rounded-xl transition-colors"
            >
              {tr ? 'Stand Ata' : 'Allocate Stand'}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
          {isAssigned && (
            <button
              onClick={onAllocate}
              data-testid={`review-${t.id}`}
              className="ml-auto inline-flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-2 rounded-xl border border-emerald-200 transition-colors"
            >
              {tr ? 'Grid\'de Gör' : 'View on Grid'}
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, capitalize }) {
  return (
    <div className="bg-gray-50 rounded-lg py-2 px-1">
      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{label}</p>
      <p className={`text-xs font-bold text-gray-800 mt-0.5 ${capitalize ? 'capitalize' : ''}`}>{value}</p>
    </div>
  );
}

function EmptyState({ tr, hasAny }) {
  return (
    <div className="text-center py-20">
      <div className="h-16 w-16 mx-auto rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
        <Package className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="font-bold text-gray-700 text-sm mb-1">
        {hasAny
          ? (tr ? 'Seçtiğiniz filtrelerle eşleşen talep yok' : 'No requests match your filters')
          : (tr ? 'Henüz stand alanı talebi yok' : 'No stand area requests yet')}
      </h3>
      <p className="text-xs text-gray-500 max-w-sm mx-auto">
        {hasAny
          ? (tr ? 'Durum veya tasarım filtresini değiştirin.' : 'Try changing status or design filter.')
          : (tr ? 'Müşteriler Customer Dashboard → Stand Request sekmesinden talep gönderdiğinde burada görünür.' : 'Requests appear here when customers submit via Customer Dashboard → Stand Request.')}
      </p>
    </div>
  );
}
